import { RESOURCES } from "./resources";
import { createBaseApi, getBaseApiEndpointObjects } from "./base.service";
import type {
  IMinisterio,
  IMinisterioAcesso,
  IMinisterioArquivo,
  IMinisterioEtiqueta,
  IMinisterioMembro,
  MinisterioDto,
} from "@/interfaces/ministerio.interface";

const ministerioBaseApi = createBaseApi<MinisterioDto>(RESOURCES.MINISTERIOS);

export const ministerioHubApi = ministerioBaseApi.injectEndpoints({
  endpoints: (build) => ({
    getMinisterioAcesso: build.query<IMinisterioAcesso, void>({
      query: () => ({ url: "/ministerios/acesso" }),
      providesTags: [{ type: RESOURCES.MINISTERIOS, id: "ACESSO" }],
    }),
    getMinisterioDetalhe: build.query<IMinisterio, number>({
      query: (id) => ({ url: `/ministerios/detalhe/${id}` }),
      providesTags: (_r, _e, id) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    addMinisterioMembro: build.mutation<
      IMinisterioMembro[],
      { id: number; EquipanteId?: number | null; ParticipanteId?: number | null; Papel?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/ministerios/${id}/membros`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: RESOURCES.MINISTERIOS, id },
        { type: RESOURCES.MINISTERIOS, id: "LIST" },
        { type: RESOURCES.MINISTERIOS, id: "ACESSO" },
      ],
    }),
    setMinisterioMembroPapel: build.mutation<
      IMinisterioMembro[],
      { id: number; equipanteId: number; Papel: "LIDER" | "MEMBRO" }
    >({
      query: ({ id, equipanteId, Papel }) => ({
        url: `/ministerios/${id}/membros/${equipanteId}/papel`,
        method: "POST",
        body: { Papel },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: RESOURCES.MINISTERIOS, id },
        { type: RESOURCES.MINISTERIOS, id: "LIST" },
        { type: RESOURCES.MINISTERIOS, id: "ACESSO" },
      ],
    }),
    removeMinisterioMembro: build.mutation<void, { id: number; equipanteId: number }>({
      query: ({ id, equipanteId }) => ({
        url: `/ministerios/${id}/membros/${equipanteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: RESOURCES.MINISTERIOS, id },
        { type: RESOURCES.MINISTERIOS, id: "LIST" },
      ],
    }),
    getMinisterioArquivos: build.query<IMinisterioArquivo[], number>({
      query: (id) => ({ url: `/ministerios/${id}/arquivos` }),
    }),
    addMinisterioArquivoLink: build.mutation<
      IMinisterioArquivo,
      { id: number; Titulo: string; LinkUrl: string; Descricao?: string; ReuniaoId?: number | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/ministerios/${id}/arquivos/link`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    setMinisterioArquivoMeta: build.mutation<
      IMinisterioArquivo,
      { id: number; arquivoId: number; Categoria?: string; Descricao?: string; ReuniaoId?: number | null }
    >({
      query: ({ id, arquivoId, ...body }) => ({
        url: `/ministerios/${id}/arquivos/${arquivoId}/meta`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    fixarMinisterioArquivo: build.mutation<
      IMinisterioArquivo[],
      { id: number; arquivoId: number; IsFixo: boolean }
    >({
      query: ({ id, arquivoId, IsFixo }) => ({
        url: `/ministerios/${id}/arquivos/${arquivoId}/fixar`,
        method: "POST",
        body: { IsFixo },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    removeMinisterioArquivo: build.mutation<void, { id: number; arquivoId: number }>({
      query: ({ id, arquivoId }) => ({
        url: `/ministerios/${id}/arquivos/${arquivoId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    postMinisterioMidias: build.mutation<IMinisterio, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/ministerios/${id}/midias`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: RESOURCES.MINISTERIOS, id },
        { type: RESOURCES.MINISTERIOS, id: "LIST" },
      ],
    }),
    createMinisterioEtiqueta: build.mutation<
      IMinisterioEtiqueta,
      { id: number; Nome: string; Cor: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/ministerios/${id}/etiquetas`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    removeMinisterioEtiqueta: build.mutation<void, { id: number; etiquetaId: number }>({
      query: ({ id, etiquetaId }) => ({
        url: `/ministerios/${id}/etiquetas/${etiquetaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
    setMinisterioMembroEtiquetas: build.mutation<
      IMinisterioMembro[],
      { id: number; equipanteId: number; etiquetas: Array<{ Id: number; Nome?: string; Cor: string }> }
    >({
      query: ({ id, equipanteId, etiquetas }) => ({
        url: `/ministerios/${id}/membros/${equipanteId}/etiquetas`,
        method: "POST",
        body: { etiquetas },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: RESOURCES.MINISTERIOS, id }],
    }),
  }),
});

export const ministerioApi = getBaseApiEndpointObjects(
  ministerioBaseApi,
  RESOURCES.MINISTERIOS,
);

export const {
  useGetMinisterioAcessoQuery,
  useGetMinisterioDetalheQuery,
  useAddMinisterioMembroMutation,
  useSetMinisterioMembroPapelMutation,
  useRemoveMinisterioMembroMutation,
  useAddMinisterioArquivoLinkMutation,
  useSetMinisterioArquivoMetaMutation,
  useFixarMinisterioArquivoMutation,
  useRemoveMinisterioArquivoMutation,
  usePostMinisterioMidiasMutation,
  useCreateMinisterioEtiquetaMutation,
  useRemoveMinisterioEtiquetaMutation,
  useSetMinisterioMembroEtiquetasMutation,
} = ministerioHubApi;
